import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Vehicle } from '../../../models/vehicle/Vehicle';
import { UPLOADABLE_DOCUMENT_TYPES } from '../../../resources/enums';
import { AlertCircle, CalendarIcon, Download, Car, RefreshCcw } from 'lucide-react';
import { previewDocument } from '../../../utils/miscFunctions';
import { IVehicleInspectionData } from '../../../models/vehicle/IVehicle';
import ImageViewer from 'react-simple-image-viewer';
import CarImage from './assets/carVector.jpg'
import { BsCarFront } from 'react-icons/bs';

type IVehicleAnalysis = {
    vehicle?: Vehicle | null;
    onRefresh?: () => void;
};

const VehicleAnalysisCard = ({ vehicle, onRefresh }: IVehicleAnalysis) => {
    const [currentImage, setCurrentImage] = useState(0);
    const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
    const progress = 50;

    const openImageViewer = useCallback((index: number) => {
        setCurrentImage(index);
        setIsImageViewerOpen(true);
    }, []);

    const closeImageViewer = () => {
        setCurrentImage(0);
        setIsImageViewerOpen(false);
    };

    const inspectionReportPdf = vehicle?.images?.find((document) => document.docType === UPLOADABLE_DOCUMENT_TYPES.INSPECTION_REPORT);

    const showLoader = progress < 100;

    return (
        <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 dark:bg-darkBg dark:border-darkBg-border p-6 mb-6">
                <h2 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText flex items-center mb-5">
                    <BsCarFront className="h-5 w-5 mr-2 text-gray-500 dark:text-gray-400" />
                    AI Analysis Result
                </h2>

                {vehicle?.inspectionData && vehicle?.score && showLoader ? (
                    <>
                        <div className="flex items-center mb-4">
                            <div className="mr-4">
                                <div
                                    className={`w-16 h-16 rounded-full flex items-center justify-center ${vehicle?.score >= 80
                                        ? 'bg-green-100 dark:bg-green-900/30'
                                        : vehicle?.score >= 50
                                            ? 'bg-yellow-100 dark:bg-yellow-900/30'
                                            : 'bg-red-100 dark:bg-red-900/30'
                                        }`}>
                                    <span
                                        className={`text-2xl font-bold ${vehicle?.score >= 80
                                            ? 'text-green-600 dark:text-green-400'
                                            : vehicle?.score >= 50
                                                ? 'text-yellow-600 dark:text-yellow-400'
                                                : 'text-red-600 dark:text-red-400'
                                            }`}
                                    >
                                        {vehicle?.score}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">{vehicle?.state}</h3>
                                <p className="text-gray-600 dark:text-darkBg-subText text-sm">
                                    {vehicle?.analysisSummary ?? '-'}
                                </p>
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[400px] grid grid-cols-1 md:grid-cols-2 gap-4">
                            {vehicle?.inspectionData?.map((vehicle, index) => (
                                <div className="flex flex-col space-y-4" key={index}>
                                    <div className="rounded-lg shadow-sm border border-gray-200 dark:border-darkBg-border overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex">
                                        <div className="w-24 h-28 bg-gray-200 dark:bg-gray-600 relative">
                                            {vehicle.image ? (
                                                <img
                                                    src={vehicle.image}
                                                    alt={`${vehicle?.partName}`}
                                                    className="w-full h-full object-cover"
                                                    onClick={() => openImageViewer(index)}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Car className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 p-4 flex flex-col justify-center">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <h3 className="text-lg font-medium text-gray-900 dark:text-darkBg-mainText">
                                                        {vehicle.partName}
                                                    </h3>
                                                    <p className="text-sm text-gray-500 dark:text-darkBg-subText">Damage type: {vehicle.damageType}</p>
                                                </div>
                                            </div>

                                            <div className="mt-2 flex items-center space-x-6">
                                                <div className="flex items-center text-sm text-gray-500 dark:text-darkBg-subText">
                                                    <CalendarIcon className="h-4 w-4 mr-1" />
                                                    {new Date(vehicle.createdOn).toLocaleDateString()}
                                                </div>

                                                {vehicle.damageNumber && (
                                                    <div className="flex items-center text-sm text-red-600 dark:text-red-400">
                                                        <AlertCircle className="h-4 w-4 mr-1" />
                                                        Damage detected
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-gray-200 dark:border-darkBg-border pt-4 mt-4">
                            <h3 className="font-medium mb-2 text-gray-900 dark:text-darkBg-mainText">Download Report</h3>
                            <div className="flex items-center">
                                {inspectionReportPdf && (
                                    <>
                                        <Download
                                            className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 cursor-pointer"
                                            onClick={() => previewDocument(inspectionReportPdf.docUrl)}
                                        />
                                        <span className="text-gray-700 dark:text-darkBg-subText">Inspection Assessment Report</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <CarAnalysisLoader onRefresh={() => onRefresh?.()} />
                )}
            </div>

            {isImageViewerOpen && vehicle?.inspectionData && (
                <ImageViewer
                    src={vehicle?.inspectionData?.map(
                        (image: IVehicleInspectionData) => image.image
                    )}
                    currentIndex={currentImage}
                    onClose={closeImageViewer}
                    disableScroll={false}
                    backgroundStyle={{
                        backgroundColor: 'rgba(0,0,0,0.9)',
                        zIndex: 999,
                    }}
                    closeOnClickOutside={true}
                />
            )}
        </>
    );
};


const CarAnalysisLoader = ({ onRefresh }: { onRefresh: () => void }) => {
    return (
        <div className="flex flex-col md:flex-row items-center p-6 bg-white dark:bg-darkBg rounded-lg border border-gray-200 dark:border-darkBg-border shadow-sm">
            {/* Left: Car + orbit */}
            <div className="relative w-60 h-60 mb-6 md:mb-0 md:mr-10 p-10">
                {/* Car image */}
                <div className="absolute inset-0 flex items-center justify-center z-10">
                    {/* <Car className="w-32 h-32 text-gray-800" /> */}
                    <img src={CarImage} className='w-32 h-auto object-cover' alt="car" />
                </div>

                {/* SVG Orbits */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                    {/* Outer orbit */}
                    <circle
                        cx="50"
                        cy="50"
                        r="45"
                        stroke="#facc15"
                        strokeWidth="0.8"
                        fill="none"
                    />
                    {/* Inner orbit */}
                    <circle
                        cx="50"
                        cy="50"
                        r="35"
                        stroke="#dcdcdc"
                        strokeWidth="0.5"
                        fill="none"
                    />

                    {/* Rotating Dots with sequential pulsing animation */}
                    <g className="origin-center animate-spin-slow">
                        {/* 4 dots at 90° intervals (0°, 90°, 180°, 270°) */}
                        <circle cx="50" cy="5" fill="#fcd34d" className="animate-pulse-dot" />
                        <circle cx="95" cy="50" fill="#fcd34d" className="animate-pulse-dot" />
                        <circle cx="50" cy="95" fill="#fcd34d" className="animate-pulse-dot" />
                        <circle cx="5" cy="50" fill="#fcd34d" className="animate-pulse-dot" />
                    </g>
                </svg>
            </div>

            {/* Right: Details */}
            <div className="space-y-3 w-full max-w-xs">
                <div className="flex flex-col flex-1">
                    <p className="text-sm font-semibold text-gray-700 dark:text-darkBg-subText">Generating analysis report...</p>
                </div>

                <button className="w-full mt-4 bg-amber-400 hover:bg-amber-500 text-white text-sm font-semibold py-2 px-4 rounded-lg shadow-md flex items-center justify-center space-x-2 transition-colors" onClick={onRefresh}>
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M11 3a1 1 0 10-2 0v7.268a2 2 0 11-2 0V3z" />
                        <path d="M3 5a2 2 0 112.83 1.82 8.001 8.001 0 1011.62-.659A2 2 0 1118 10.5V12a1 1 0 11-2 0v-1.5a6 6 0 10-12 0V12a1 1 0 01-2 0V5z" />
                    </svg>
                    <span>Reload Analysis</span>
                </button>
            </div>
        </div>
    );
};

// Add this to your global CSS or in a style tag
const styles = `
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .animate-spin-slow {
    animation: spin-slow 12s linear infinite;
  }
  
  @keyframes pulse {
    0%, 100% { r: 2.5; opacity: 0.5; }
    50% { r: 5; opacity: 1; }
  }
  
  .animate-pulse-dot {
    r: 2.5;
    opacity: 0.5;
  }
  
  .animate-pulse-dot:nth-child(1) { 
    animation: pulse 2.4s ease-in-out infinite;
  }
  .animate-pulse-dot:nth-child(2) { 
    animation: pulse 2.4s ease-in-out 0.6s infinite;
  }
  .animate-pulse-dot:nth-child(3) { 
    animation: pulse 2.4s ease-in-out 1.2s infinite;
  }
  .animate-pulse-dot:nth-child(4) { 
    animation: pulse 2.4s ease-in-out 1.8s infinite;
  }
`;

// Add the styles to the document head
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
}

export default VehicleAnalysisCard;
